import { useMemo } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeftIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useReportDrill, type DrillPosition } from "../lib/receipts";
import { ReceiptListExport } from "../components/ReceiptListExport";
import { formatCurrency, formatDate } from "../lib/format";

// Client-CSV der Positionen — bewusst NICHT der PDF/ZIP-Export aus export.ts (der ist
// beleg-ID-basiert und fuer Positionen sinnlos). Semikolon + BOM = Excel-DE-kompatibel.
function downloadPositionsCsv(positions: DrillPosition[], label: string) {
  // „Effektiv“ kommt fertig vom Server (cent-genaue Verteilung des Bon-Rabatts, bei Teilen der Teilbetrag) — hier nichts nachrechnen.
  const header = ["Datum", "Haendler", "Bezeichnung", "Kategorie", "Menge", "Einheit", "Brutto", "Rabatt", "Effektiv", "Gruppe", "Anteil %"];
  const esc = (v: string | number | null) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const eur = (n: number) => n.toFixed(2).replace(".", ",");
  const rows = positions.map((p) => [
    (p.receiptDate ?? "").slice(0, 10), // YYYY-MM-DD ohne Zeitanteil (Excel-datumstauglich)
    p.vendor ?? "",
    p.description,
    p.categoryName,
    p.quantity ?? "",
    p.unit ?? "",
    eur(p.priceGross),
    eur(p.productDiscount),
    eur(p.effective),
    p.gruppe,
    p.anteilBp === null ? "" : String(p.anteilBp / 100).replace(".", ","),
  ]);
  const csv = [header, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Positionen_${label}.csv`.replace(/[^\w.-]+/g, "_");
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function DrillDownPage() {
  const [sp] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const categoryId = sp.get("categoryId") ?? undefined;
  const subcategoryId = sp.get("subcategoryId") ?? undefined;
  const subSubcategoryId = sp.get("subSubcategoryId") ?? undefined;
  const vendor = sp.get("vendor") ?? undefined;
  const from = sp.get("from") ?? undefined;
  const to = sp.get("to") ?? undefined;
  const gruppe = sp.get("gruppe") ?? undefined;
  const nurUnbestaetigt = sp.get("nurUnbestaetigt") === "1";
  // Migration 0010/0011: Achsen-Filter aus URL übernehmen (1:1 wie vom Tree/AuswertungenPage gesetzt).
  // direction muss "neutral" enthalten — sonst rutscht ein Tree-Klick auf den Neutral-Bucket
  // silent auf undefined und das Backend filtert nicht nach direction.
  const directionRaw = sp.get("direction");
  const stabilityRaw = sp.get("stability");
  const direction =
    directionRaw === "expense" ||
    directionRaw === "income" ||
    directionRaw === "neutral" ||
    directionRaw === "all"
      ? directionRaw
      : undefined;
  const stability =
    stabilityRaw === "fixed" ||
    stabilityRaw === "variable" ||
    stabilityRaw === "none" ||
    stabilityRaw === "all"
      ? stabilityRaw
      : undefined;
  const clickedName = sp.get("name") ?? undefined;

  const { data, isLoading, error } = useReportDrill({
    categoryId,
    subcategoryId,
    subSubcategoryId,
    vendor,
    from,
    to,
    gruppe,
    nurUnbestaetigt,
    direction,
    stability,
  });

  const labelAusServer = data?.label && data.label !== "Auswertung" ? data.label : undefined;
  const heading = clickedName ?? labelAusServer ?? (gruppe ? `${gruppe}${nurUnbestaetigt ? " — nur Vorschläge" : ""}` : "Auswertung");

  // navIds fuer B3: eindeutige Beleg-IDs in Listenreihenfolge — so blaettern die ←/→-Pfeile
  // in der Detailansicht durch genau diese Drill-Liste.
  const navIds = useMemo(
    () => (data?.kind === "positions" ? [...new Set(data.positions.map((p) => p.receiptId))] : undefined),
    [data],
  );

  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/auswertungen");
  };

  return (
    <div className="px-5 py-6 md:px-10 md:py-8 max-w-5xl mx-auto animate-fade-in">
      <button
        onClick={goBack}
        className="inline-flex items-center gap-1 text-[15px] text-ios-blue hover:text-ios-blueHover mb-3"
      >
        <ChevronLeftIcon className="w-5 h-5" /> Auswertungen
      </button>

      <header className="mb-4">
        <h1 className="text-[24px] md:text-[28px] font-semibold tracking-tight">{heading}</h1>
        <p className="text-[14px] text-ios-muted mt-0.5">
          {from && to ? `${from} – ${to}` : "Gesamter Zeitraum"}
        </p>
      </header>

      {isLoading ? (
        <div className="ios-card p-10 text-center text-ios-muted">Lade…</div>
      ) : error || !data ? (
        <div className="ios-card p-10 text-center text-ios-muted">Konnte die Liste nicht laden.</div>
      ) : data.kind === "receipts" ? (
        <>
          <div className="ios-card px-4 py-3 mb-3 flex items-center justify-between">
            <span className="text-[14px] text-ios-muted">{data.receipts.length} Belege</span>
            <span className="text-[17px] font-semibold tabular-nums">{formatCurrency(data.total)}</span>
          </div>
          {data.receipts.length === 0 ? (
            <div className="ios-card p-10 text-center text-ios-muted">Keine Belege in diesem Zeitraum.</div>
          ) : (
            <ReceiptListExport receipts={data.receipts} />
          )}
        </>
      ) : (
        <>
          <div className="ios-card px-4 py-3 mb-3 flex items-center justify-between gap-3">
            <span className="text-[14px] text-ios-muted">
              {data.positions.length} Positionen ·{" "}
              <span className="font-semibold text-ios-ink tabular-nums">{formatCurrency(data.total)}</span>
            </span>
            <button
              type="button"
              onClick={() => downloadPositionsCsv(data.positions, heading)}
              disabled={data.positions.length === 0}
              className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-blue text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1 shrink-0"
            >
              <ArrowDownTrayIcon className="w-4 h-4" /> CSV
            </button>
          </div>

          {data.positions.length === 0 ? (
            <div className="ios-card p-10 text-center text-ios-muted">Keine Positionen in diesem Zeitraum.</div>
          ) : (
            <ul className="ios-card divide-y divide-ios-line/40 overflow-hidden">
              {data.positions.map((p) => (
                <li key={`${p.lineItemId}|${p.gruppe}|${p.anteilBp ?? "ganz"}`}>
                  <Link
                    to={`/receipts/${p.receiptId}`}
                    state={navIds ? { navIds } : undefined}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-ios-bg/40 transition active:bg-ios-bg/60"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] truncate">{p.description}</div>
                      <div className="text-[12px] text-ios-muted truncate">
                        {p.vendor ?? "Unbekannt"}
                        {p.receiptDate ? ` · ${formatDate(p.receiptDate)}` : ""}
                        {p.quantity != null ? ` · ${p.quantity}${p.unit ? ` ${p.unit}` : ""}` : ""}
                      </div>
                      {p.anteilBp !== null && (
                        <div className="text-[12px] text-ios-blue">
                          ✂ Teil einer aufgeteilten Position: {(p.anteilBp / 100).toLocaleString("de-DE")} % ({p.gruppe})
                        </div>
                      )}
                    </div>
                    <div className="text-[15px] font-medium tabular-nums shrink-0">{formatCurrency(p.effective)}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
